import Api from "./wlgpi-api";

export default class {
  constructor(homeUrl, nonce, api = "frm", lang = null) {
    this.api = new Api("wlgpi/" + api + "/custom", homeUrl, nonce, lang);
  }

  async businessMailPrices(uuid, period, licences, licenceType) {
    const response = await this.api.get("business-mail-prices", {
      uuid,
      period,
      licences,
      licenceType,
    });

    if (response.error) {
      console.error(response.error);

      return response;
    }

    return response;
  }

  async storagePrices(period, capacity) {
    const response = await this.api.get("storage-prices", { period, capacity });

    if (response.error) {
      console.error(response.error);

      return response;
    }

    return response;
  }

  async vpsPrices(
    period,
    cpu,
    ram,
    capacity,
    osName,
    osTemplate,
    windowsLicencePrice,
    windowsLicenceText,
    uuid
  ) {
    const response = await this.api.get("vps-prices", {
      period,
      cpu,
      ram,
      capacity,
      osName,
      osTemplate,
      windowsLicencePrice,
      windowsLicenceText,
      uuid,
    });

    if (response.error) {
      console.error(response.error);

      return response;
    }

    return response;
  }

  async privateServerPrices(
    uuid,
    {
      period,
      cpu,
      ram,
      capacity,
      osTemplate,
      windowsLicencePrice,
      windowsLicenceText,
    }
  ) {
    const response = await this.api.get("private-server-prices", {
      period,
      cpu,
      ram,
      capacity,
      osTemplate,
      uuid,
      windowsLicencePrice,
      windowsLicenceText,
    });

    if (response.error) {
      console.error(response.error);

      return response;
    }

    return response;
  }

  async vdcPrices(period, cpu, ram, capacity, ip, storage) {
    const response = await this.api.get("vdc-prices", {
      period,
      cpu,
      ram,
      capacity,
      ip,
      storage,
    });

    if (response.error) {
      console.error(response.error);

      return response;
    }

    return response;
  }

  async hostingPrices(uuid, data = {}) {
    if (typeof uuid !== "string" || uuid.trim() === "") {
      throw new Error("UUID is required and must be a non-empty string");
    }

    const requestParams = { uuid };

    for (const [key, value] of Object.entries(data)) {
      if (typeof value === "number" && !isNaN(value)) {
        requestParams[key] = value;
      }
    }

    const response = await this.api.get("hosting-prices", requestParams);

    if (response.error) {
      console.error(response.error);
    }

    return response;
  }

  async websiteBuilderPrices(uuid, data = {}) {
    // Forward all fields from `data` so additional boolean `addon_*` flags
    // (e.g. addon_automatic_backups) reach the REST endpoint. The legacy
    // hardcoded keys (period, addon_email_storage*, addon_ecommerce_subscription)
    // pass through unchanged.
    const response = await this.api.get("website-builder-prices", {
      uuid,
      ...data,
    });

    if (response.error) {
      console.error(response.error);

      return response;
    }

    return response;
  }

  async webnodePrices(uuid, data = {}) {
    // Forward all fields from `data` so additional boolean `addon_*` flags
    // (e.g. addon_automatic_backups) reach the REST endpoint.
    const response = await this.api.get("webnode-prices", {
      uuid,
      ...data,
    });

    if (response.error) {
      console.error(response.error);

      return response;
    }

    return response;
  }

  /**
   * Get online marketing prices
   *
   * @param {string} uuid - Product UUID
   * @param {Object} data - Additional parameters
   * @param {number} data.period - Period in months (1 or 12)
   * @param {Array} data.packageAddons - Array of package addons
   * @returns {Promise<Object>} Price information
   */
  async onlineMarketingPrices(uuid, data = {}) {
    const { period = 1, ...params } = data;

    // Create request params with period and uuid
    const requestParams = {
      uuid,
      period,
    };

    // Add any addon_ parameters from the data object
    Object.entries(params).forEach(([key, value]) => {
      if (key.startsWith("addon_") && value === true) {
        requestParams[key] = value;
      }
    });

    const response = await this.api.get(
      "online-marketing-prices",
      requestParams
    );

    if (response.error) {
      console.error(response.error);
    }

    return response;
  }

  async cookieCompliancePrices(uuid, data = {}) {
    const { period = 1, ...params } = data;

    const response = await this.api.get("cookie-compliance-prices", {
      uuid,
      period,
      ...params,
    });

    if (response.error) {
      console.error(response.error);
    }

    return response;
  }
}
